import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, LayoutDashboard, Search as SearchIcon, Megaphone, Coins, Shield, Compass, Users, Newspaper } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationsBell } from "@/components/NotificationsBell";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLink = (to, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      onClick={() => setOpen(false)}
      className={`text-sm font-semibold transition-colors hover:text-brand ${
        location.pathname === to ? "text-brand" : "text-slate-600"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200/60 shadow-sm">
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-brand flex items-center justify-center">
            <div className="h-3.5 w-3.5 rounded-full border-2 border-white" />
          </div>
          <span className="font-display font-extrabold text-lg tracking-tight text-slate-900">
            IndiaBusiness<span className="text-brand">Circle</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          {navLink("/discover", "Discover", "nav-discover")}
          {navLink("/feed", "Feed", "nav-feed")}
          {navLink("/news", "News", "nav-news")}
          {user ? (
            <>
              {navLink("/network", "Network", "nav-network")}
              {user.role === "admin" && navLink("/admin", "Admin", "nav-admin")}
              <NotificationsBell />
              <span data-testid="nav-credits" className="inline-flex items-center gap-1.5 rounded-full bg-brand-light px-3 py-1 text-sm font-bold text-brand">
                <Coins size={14} /> {user.credits ?? 0}
              </span>
              {navLink("/dashboard", "My Profile", "nav-dashboard")}
              <Button data-testid="nav-logout-btn" onClick={doLogout} variant="ghost" className="text-slate-600 gap-2">
                <LogOut size={16} /> Logout
              </Button>
            </>
          ) : (
            <>
              {navLink("/login", "Login", "nav-login")}
              <Button data-testid="nav-signup-btn" onClick={() => navigate("/register")} className="rounded-full bg-brand hover:bg-brand-hover px-6 font-semibold transition-transform hover:-translate-y-0.5">
                Claim Your Business
              </Button>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden text-slate-700">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-5 py-4 flex flex-col gap-4">
          <Link to="/discover" data-testid="nav-discover-mobile" onClick={() => setOpen(false)} className="flex items-center gap-2 text-slate-700 font-semibold">
            <Compass size={18} /> Discover
          </Link>
          <Link to="/feed" data-testid="nav-feed-mobile" onClick={() => setOpen(false)} className="flex items-center gap-2 text-slate-700 font-semibold">
            <Megaphone size={18} /> Feed
          </Link>
          <Link to="/news" data-testid="nav-news-mobile" onClick={() => setOpen(false)} className="flex items-center gap-2 text-slate-700 font-semibold">
            <Newspaper size={18} /> News
          </Link>
          {user ? (
            <>
              <Link to="/network" data-testid="nav-network-mobile" onClick={() => setOpen(false)} className="flex items-center gap-2 text-slate-700 font-semibold">
                <Users size={18} /> Network
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin-mobile" onClick={() => setOpen(false)} className="flex items-center gap-2 text-slate-700 font-semibold">
                  <Shield size={18} /> Admin
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard-mobile" onClick={() => setOpen(false)} className="flex items-center gap-2 text-slate-700 font-semibold">
                <LayoutDashboard size={18} /> My Profile <span className="inline-flex items-center gap-1 text-brand"><Coins size={14} /> {user.credits ?? 0}</span>
              </Link>
              <Button data-testid="nav-logout-mobile" onClick={doLogout} variant="outline" className="w-full gap-2">
                <LogOut size={16} /> Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-mobile" onClick={() => setOpen(false)} className="text-slate-700 font-semibold">Login</Link>
              <Button data-testid="nav-signup-mobile" onClick={() => { setOpen(false); navigate("/register"); }} className="w-full rounded-full bg-brand hover:bg-brand-hover font-semibold">Claim Your Business</Button>
            </>
          )}
        </div>
      )}
    </header>
  );
};
